import React, { useState, useRef, useEffect } from 'react';
import { Dot, Tag, Paperclip, X } from 'lucide-react';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import { HubBoardIcon } from '../utils/hubSidebarUtils';

function HubCategoryPickerDropdown({ open, children }) {
  if (!open) return null;
  return (
    <div className="board-search-filters-popover board-search-filters-popover--simple hub-sugestao-category-dropdown">
      {children}
    </div>
  );
}

/**
 * Lista de sugestões (posts): campo "Compartilhe sua sugestão" expansível com formulário, filtros, busca e cards.
 */
const SugestoesList = ({
  onNewSuggestionClick,
  posts,
  onPostClick,
  onVoteClick,
  votingPostId,
  getStatusLabel,
  expanded = false,
  createTitle = '',
  createDetails = '',
  createCategory = '',
  categories = [],
  onTitleChange,
  onDetailsChange,
  onCategoryChange,
  onCancelCreate,
  onCreatePost,
  createPostLoading = false,
  attachmentItems = [],
  onOpenAttachPicker,
  onCancelAttachment,
  boardName = ''
}) => {
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categoryRef = useRef(null);

  useEffect(() => {
    const onOutside = (e) => { if (categoryRef.current && !categoryRef.current.contains(e.target)) setCategoryOpen(false); };
    if (categoryOpen) document.addEventListener('click', onOutside);
    return () => document.removeEventListener('click', onOutside);
  }, [categoryOpen]);

  const categoryList = categories || [];
  const selectedCategoryLabel = createCategory
    ? (categoryList.find((c) => c.id === createCategory)?.name || createCategory)
    : 'Selecione uma categoria';

  return (
  <div className="hub-sugestoes-list">
    <div className={`hub-sugestao-expand-wrap${expanded ? ' is-expanded' : ''}`}>
    <div
      className={`hub-sugestao-expand${expanded ? ' is-expanded' : ''}`}
    >
      {!expanded && (
        <div className="hub-sugestao-expand-trigger">
          <input
            type="text"
            className="hub-sugestao-expand-input"
            placeholder="Compartilhe sua sugestão..."
            value=""
            readOnly
            onClick={onNewSuggestionClick}
          />
          <button
            type="button"
            onClick={onNewSuggestionClick}
            style={{
              position: 'absolute',
              top: '50%',
              right: 14,
              transform: 'translateY(-50%)',
              width: 27,
              height: 27,
              borderRadius: 5,
              background: '#525df9',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14
            }}
          >
            <i className="bi bi-plus-lg" />
          </button>
        </div>
      )}
      {expanded && (
        <div style={{ padding: '16px 16px 0', position: 'relative' }}>
          {createPostLoading && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: 'linear-gradient(90deg, #525DF9 0%, #525DF9 30%, transparent 30%)',
                backgroundSize: '200% 100%',
                animation: 'hub-create-progress 1.2s ease-in-out infinite',
                borderRadius: '0 0 4px 4px',
                zIndex: 10
              }}
              aria-hidden
            />
          )}
          <div style={{ marginBottom: 12, opacity: createPostLoading ? 0.7 : 1, pointerEvents: createPostLoading ? 'none' : 'auto' }}>
            <div className="hub-sugestao-create-title-row">
              <input
                type="text"
                placeholder="Título curto e descritivo"
                value={createTitle}
                onChange={(e) => onTitleChange && onTitleChange(e.target.value)}
                readOnly={createPostLoading}
                className="hub-body-text hub-sugestao-create-title-input"
              />
              {boardName ? (
                <div className="hub-sugestao-create-board-hint" aria-label={`Feedback sendo criado em ${boardName}`}>
                  <span className="hub-sugestao-create-board-hint-label">Feedback sendo criado em</span>
                  <i className="bi bi-chevron-right hub-sugestao-create-board-hint-sep" aria-hidden />
                  <span className="hub-sugestao-create-board-target">
                    <HubBoardIcon boardName={boardName} className="hub-sugestao-create-board-target-icon" />
                    <span className="hub-sugestao-create-board-target-name">{boardName}</span>
                  </span>
                </div>
              ) : null}
            </div>
          </div>
          <div style={{ marginBottom: 12, opacity: createPostLoading ? 0.7 : 1, pointerEvents: createPostLoading ? 'none' : 'auto' }}>
            <label className="hub-sugestao-create-label">Detalhes</label>
            <textarea
              placeholder="Informações adicionais..."
              value={createDetails}
              onChange={(e) => onDetailsChange && onDetailsChange(e.target.value)}
              readOnly={createPostLoading}
              rows={4}
              className="hub-sugestao-create-details"
            />
          </div>
          <div style={{ marginBottom: 16, opacity: createPostLoading ? 0.7 : 1, pointerEvents: createPostLoading ? 'none' : 'auto' }}>
            <label className="hub-sugestao-create-label">Categoria</label>
            <div className="hub-sugestao-category-anchor hub-category-select" ref={categoryRef}>
              <button
                type="button"
                className={`hub-category-select-trigger${createCategory ? '' : ' is-placeholder'}`}
                onClick={() => !createPostLoading && setCategoryOpen((o) => !o)}
                disabled={createPostLoading}
                aria-expanded={categoryOpen}
                aria-haspopup="listbox"
              >
                <span>{selectedCategoryLabel}</span>
                <span className="hub-category-select-chevron" style={{ marginRight: 4, transform: categoryOpen ? 'rotate(180deg)' : 'none' }} aria-hidden>▼</span>
              </button>
              <HubCategoryPickerDropdown open={categoryOpen}>
                <BoardSearchSimplePickerPanel
                  showSearch={false}
                  items={categoryList}
                  getKey={(c) => c.id}
                  getLabel={(c) => c.name || 'Geral'}
                  isSelected={(c) => createCategory === c.id}
                  onToggle={(c) => {
                    onCategoryChange && onCategoryChange(c.id);
                    setCategoryOpen(false);
                  }}
                  listAriaLabel="Selecionar categoria"
                  emptyMessage="Nenhuma categoria disponível"
                />
              </HubCategoryPickerDropdown>
            </div>
          </div>
          <div className="hub-sugestao-create-actions-bar">
            <div className="hub-sugestao-create-actions-inner">
              {!!attachmentItems.length && (
                <div className="hub-sugestao-create-attachments">
                  {attachmentItems.map((item) => (
                    <div key={item.localId} className="hub-sugestao-create-attachment-chip">
                      <span className="hub-sugestao-create-attachment-name">{item.file?.name || 'imagem'}</span>
                      <span className={`hub-sugestao-create-attachment-status${item.status === 'error' ? ' is-error' : ''}`}>
                        {item.status === 'uploading' ? `${Math.round(item.progress || 0)}%` : item.status === 'done' ? 'Concluído' : item.status === 'error' ? 'Erro' : 'Pendente'}
                      </span>
                      <button
                        type="button"
                        className="hub-sugestao-create-attachment-remove"
                        onClick={() => onCancelAttachment && onCancelAttachment(item.localId)}
                        title="Remover"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="hub-sugestao-create-actions-btns">
                <button
                  type="button"
                  className="hub-sugestao-create-btn hub-sugestao-create-btn--secondary"
                  onClick={onOpenAttachPicker}
                  disabled={createPostLoading}
                >
                  <Paperclip size={14} />
                  Anexar imagens
                </button>
                <button
                  type="button"
                  className="hub-sugestao-create-btn hub-sugestao-create-btn--secondary"
                  onClick={onCancelCreate}
                  disabled={createPostLoading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className={`hub-sugestao-create-btn hub-sugestao-create-btn--primary${createPostLoading ? ' is-busy' : ''}`}
                  onClick={onCreatePost}
                  disabled={createPostLoading}
                >
                  {createPostLoading && (
                    <i className="bi bi-arrow-repeat hub-vote-spin" aria-hidden />
                  )}
                  {createPostLoading ? 'Criando...' : 'Criar postagem'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>

    <div className="hub-sugestoes-list-panel">
      {posts.length === 0 && (
        <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
          Nenhuma sugestão ainda. Clique em + para criar a primeira.
        </div>
      )}
      {posts.map((post, index) => (
        <article
          key={post.id}
          role="button"
          tabIndex={0}
          onClick={() => onPostClick(post)}
          onKeyDown={(e) => e.key === 'Enter' && onPostClick(post)}
          className={`hub-sugestao-card${index < posts.length - 1 ? ' has-divider' : ''}`}
        >
          <div
            role="button"
            tabIndex={votingPostId === post.id ? -1 : 0}
            className={`hub-sugestao-vote board-clickup-icon-btn-anchor${post.votedByMe ? ' is-voted' : ''}${votingPostId === post.id ? ' is-busy' : ''}`}
            onClick={(e) => { e.stopPropagation(); if (votingPostId !== post.id) onVoteClick(post); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); if (votingPostId !== post.id) onVoteClick(post); } }}
          >
            {votingPostId === post.id ? (
              <i className="bi bi-arrow-repeat hub-vote-spin hub-vote-spin--lg" aria-hidden />
            ) : (
              <>
                <button
                  type="button"
                  className="hub-vote-chevron-btn"
                  onClick={(e) => { e.stopPropagation(); onVoteClick(post); }}
                >
                  <i className="bi bi-chevron-up" />
                </button>
                <span className="hub-vote-count">{post.voteCount ?? 0}</span>
              </>
            )}
            <span className="board-clickup-tooltip board-clickup-tooltip--bottom hub-feedback-tooltip--wrap" role="tooltip">
              {post.votedByMe ? 'Cancelar meu voto neste feedback' : 'Votar neste feedback'}
            </span>
          </div>
          <div className="hub-sugestao-card-body">
            <h3 className="hub-sugestao-card-title">{post.title}</h3>
            <p className="hub-body-text hub-sugestao-card-preview">{post.details || post.description}</p>
            <div className="hub-muted hub-sugestao-card-meta" role="presentation" onClick={(e) => e.stopPropagation()}>
              <span><i className="bi bi-chat" style={{ marginRight: 4 }} />{post.commentCount ?? 0}</span>
              {post.categoryName && (
                <>
                  <Dot size={12} className="hub-meta-dot" aria-hidden />
                  <span className="hub-sugestao-card-category">
                    <Tag size={12} strokeWidth={2} style={{ flexShrink: 0 }} />
                    {post.categoryName}
                  </span>
                </>
              )}
              {post.status && String(post.status).toLowerCase() !== 'open' && (
                <>
                  <Dot size={12} className="hub-meta-dot" aria-hidden />
                  <span className="hub-status-pill hub-status-pill--status">
                    {getStatusLabel ? getStatusLabel(post.status) : post.status}
                  </span>
                </>
              )}
              {post.estimated && (
                <>
                  <Dot size={12} className="hub-meta-dot" aria-hidden />
                  <span className="hub-status-pill hub-status-pill--eta">
                    {post.estimated}
                  </span>
                </>
              )}
            </div>
          </div>
        </article>
      ))}
    </div>
  </div>
  );
};

export default SugestoesList;
